import { useState } from 'react';

const STATUS_STYLES = {
  available: 'bg-green-100 text-green-800 border-green-200',
  booked:    'bg-amber-100 text-amber-800 border-amber-200',
  off:       'bg-gray-100 text-gray-500 border-gray-200',
};

const STATUS_LABELS = { available: 'Available', booked: 'Booked', off: 'Off' };

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay(); // 0=Sun
}
function pad(n) {
  return String(n).padStart(2, '0');
}

/**
 * Props:
 *   availability: [{ date: 'YYYY-MM-DD', status }]
 *   editable: boolean (caddie editing their own calendar)
 *   onSave: async ({ dates: [{ date, status }] }) => void
 */
export default function AvailabilityCalendar({ availability = [], editable = false, onSave, onMonthChange }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [pending, setPending] = useState({}); // date -> status
  const [saving, setSaving] = useState(false);

  // Merge server data + local pending edits
  const avMap = {};
  for (const { date, status } of availability) avMap[date] = status;
  const merged = { ...avMap, ...pending };

  function prevMonth() {
    const date = new Date(viewYear, viewMonth - 1, 1);
    setViewYear(date.getFullYear());
    setViewMonth(date.getMonth());
    onMonthChange?.(`${date.getFullYear()}-${pad(date.getMonth() + 1)}`);
  }
  function nextMonth() {
    const date = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(date.getFullYear());
    setViewMonth(date.getMonth());
    onMonthChange?.(`${date.getFullYear()}-${pad(date.getMonth() + 1)}`);
  }

  function handleDayClick(dateStr) {
    if (!editable) return;
    const current = merged[dateStr];
    // Every click must produce a persisted status. Deleting a pending "off"
    // edit would reveal the server-side "off" value again and trap the date.
    const cycle = { undefined: 'available', available: 'booked', booked: 'off', off: 'available' };
    const next = cycle[current ?? 'undefined'];
    setPending((p) => ({ ...p, [dateStr]: next }));
  }

  async function handleSave() {
    if (!onSave || Object.keys(pending).length === 0) return;
    setSaving(true);
    try {
      await onSave({ dates: Object.entries(pending).map(([date, status]) => ({ date, status })) });
      setPending({});
    } finally {
      setSaving(false);
    }
  }

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="select-none">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} className="p-1 rounded hover:bg-gray-100">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="font-semibold text-gray-800">{monthLabel}</span>
        <button onClick={nextMonth} className="p-1 rounded hover:bg-gray-100">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 mb-1">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d) => (
          <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Blank cells for first week offset */}
        {Array.from({ length: firstDay }).map((_, i) => <div key={`blank-${i}`} />)}

        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateStr = `${viewYear}-${pad(viewMonth + 1)}-${pad(day)}`;
          const status = merged[dateStr];
          const isPast = new Date(dateStr) < new Date(today.toDateString());
          const hasPending = pending[dateStr] !== undefined;

          return (
            <button
              key={dateStr}
              onClick={() => handleDayClick(dateStr)}
              disabled={!editable || isPast}
              title={status ? STATUS_LABELS[status] : editable && !isPast ? 'Click to set availability' : ''}
              className={[
                'aspect-square rounded-lg text-xs font-medium border transition-all',
                status ? STATUS_STYLES[status] : 'border-transparent text-gray-600 hover:bg-gray-50',
                isPast ? 'opacity-30 cursor-default' : editable ? 'cursor-pointer' : 'cursor-default',
                hasPending ? 'ring-2 ring-brand-500 ring-offset-1' : '',
              ].join(' ')}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-3">
        {Object.entries(STATUS_LABELS).map(([s, label]) => (
          <div key={s} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm border ${STATUS_STYLES[s]}`} />
            <span className="text-xs text-gray-500">{label}</span>
          </div>
        ))}
      </div>

      {/* Save button for editable mode */}
      {editable && Object.keys(pending).length > 0 && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary mt-4 w-full"
        >
          {saving ? 'Saving…' : `Save ${Object.keys(pending).length} change${Object.keys(pending).length > 1 ? 's' : ''}`}
        </button>
      )}
    </div>
  );
}
